/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToOrderTemplateViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxShopSimilarLookViewProps
} from '@msdyn365-commerce-modules/buybox';
import { IBuyboxViewProps } from '@msdyn365-commerce-modules/buybox';
import { IBuyboxFindInStoreViewProps } from '@msdyn365-commerce-modules/buybox';

//----------------------------------------------------------
// @CUSTOM: Utility imports for customizations
//----------------------------------------------------------
import { convertCustomerAttributes, convertProductAttributes } from '../../../utilities/data-attribute-parser';
import { attrNames, labelResources } from '../../../utilities/global-constants';
import { varReplace } from '../../../utilities/variable-replacer';
//----------------------------------------------------------

//----------------------------------------------------------
// @CUSTOM: Added imports for additional config from
// extended definition
//----------------------------------------------------------
import { IProductInventoryInformation } from '@msdyn365-commerce-modules/retail-actions';
import { RichText, RichTextComponent } from '@msdyn365-commerce/core';
import { IBuyboxProps } from '../definition-extensions/buybox.ext.props.autogenerated';
import { IBuyboxData } from './buybox.data';
//----------------------------------------------------------

// @CUSTOM: imports analytics
import { publish } from '../../../utilities/analytics/analytics-dispatcher';
import { SingleProductEvent } from '../../../utilities/analytics/analytics-events';

let sentAnalytics = false;
//----------------------------------------------------------

const BuyboxView: React.FC<IBuyboxViewProps & IBuyboxProps<IBuyboxData>> = props => {   // @CUSTOM: Added extended definition props and data
    const {
        ModuleProps,
        MediaGalleryContainerProps,
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        addToWishlist,
        configure,
        description,
        findInStore,
        quantity,
        price,
        title,
        rating,
        inventoryLabel,
        shopSimilarLook,
        keyInPrice,
        shopSimilarDescription,
        unitOfMeasure,
        productComparisonButton,
        slots,  // @CUSTOM: Added extended definition slots
        data,    // @CUSTOM: To get product data for description and item ID
        resources // @CUSTOM: To get custom buybox.definition resources for pricing label
    } = props;

    // @CUSTOM: PDP view analytics
    if (!sentAnalytics && typeof window !== 'undefined') {

        const product = props.data.product.result;

        setTimeout(() => {
            // @CUSTOM: send pdpview event here to ensure subscribe has fired
            if (product && !sentAnalytics) {
                sentAnalytics = true;

                const productPrice = props.data.productPrice.result;

                const analyticsProductData: SingleProductEvent = {
                    itemID: product.ItemId || '',
                    name: product.Name || '',
                    discount: productPrice?.DiscountAmount,
                    variant: '',
                    quantity: 1,
                    price: productPrice?.BasePrice || product.Price,
                    currency: props.context.request.channel?.Currency || 'USD'
                };
                publish('productDetailView', analyticsProductData);
            }
        }, 0);
    }

    //------------------------------------------------------
    // @CUSTOM: Get converted product attributes
    //------------------------------------------------------
    const productSpecificationData = data?.productSpecificationData?.result;
    const productAttributes = productSpecificationData && convertProductAttributes(productSpecificationData);
    //-------------------------------------------------------

    //------------------------------------------------------
    // @CUSTOM: Get customer attributes for license checking
    //------------------------------------------------------
    const customerData = data?.customerInformation?.result?.Attributes;
    const customerAttributes = customerData && convertCustomerAttributes(customerData);

    // @CUSTOM: override short description field with product attribute
    let productDescriptionOverride;
    if (props.config.descriptionOverride && productAttributes) {
        const productDescriptionOverrideText = productAttributes[props.config.descriptionOverride];
        if (productDescriptionOverrideText) {
            productDescriptionOverride = <p className='ms-buybox__product-description'>{productDescriptionOverrideText}</p>;
        }
    }

    //------------------------------------------------------
    // @CUSTOM: Getting product item ID for SKU display
    //------------------------------------------------------
    const productData = data?.product?.result;
    const itemId = productData?.ItemId;
    const skuDisplay = (
        <div className='ms-buybox__sku'>
            <span className='ms-buybox__sku-label'>{labelResources.sku}</span>
            <span className='ms-buybox__sku-value'>{itemId}</span>
        </div>
    );

    //------------------------------------------------------
    // @CUSTOM: Getting user authentication status and setting pricing label for it
    //------------------------------------------------------
    const signedIn = props.context.request.user.isAuthenticated;
    const pricingOrg = (
        <div className='ms-buybox__pricingOrg'>
            <span className='ms-buybox__pricingOrg-label'>{signedIn ? resources.pricingOrgSignedIn : resources.pricingOrgSignedOut}</span>
        </div>
    )
    //------------------------------------------------------

    // @CUSTOM: backorder customization
    const productAvail = props.data.backorderAvail.result;

    // @CUSTOM: unbuyable customization
    const unbuyable = productAttributes && productAttributes[attrNames.notBuyable] as string;

    // @CUSTOM: duplicate license customization
    const isLicense = productAttributes && (productAttributes[attrNames.productType] === attrNames.troveAnnualLicense);
    const licenseParticipant = customerAttributes && customerAttributes[attrNames.troveParticipant];
    const licenseDuplicate = (isLicense && licenseParticipant);

    // @CUSTOM: signed out addtocart replacement
    const isSignedIn = props.context.request.user.isAuthenticated;

    return (
        <Module {...ModuleProps}>
            <Node {...MediaGalleryContainerProps}>{props.mediaGallery}</Node>
            <Node {...ProductInfoContainerProps}>
                {title}
                {/* @CUSTOM: price moved to below description */}
                {/* @CUSTOM: PowerReviews slot */}
                {slots.reviewSnippet?.length && _renderSlotItems(slots.reviewSnippet)}
                {unitOfMeasure}
                {props.bulkPurchaseLink}
                {/* @CUSTOM: override short description field with product attribute */}
                {productDescriptionOverride || description}
                {/* @CUSTOM: Kit selector module slot */}
                {slots.kitSelector?.length && _renderSlotItems(slots.kitSelector)}
                {slots.kitCustomizer?.length && _renderSlotItems(slots.kitCustomizer)}
                {/* @CUSTOM: Product SKU display */}
                {skuDisplay}
                {/* @CUSTOM: Pricing organization context label*/}
                {pricingOrg}
                {price}
                {rating}
                {configure && _renderConfigure(configure)}
                {keyInPrice && _renderKeyInPrice(keyInPrice)}
                {/* @CUSTOM: backorder customization */}
                {productAvail && _renderBackOrder(productAvail, props.config.backOrderText)}
                {/* @CUSTOM: Unbuyable customization */}
                {unbuyable && (
                    <div className='ms-buybox__unbuyable'>
                        <RichTextComponent className='ms-buybox__unbuyable-text' text={unbuyable} />
                    </div>
                )}

                {/* @CUSTOM: Duplicate license warning */}
                {!unbuyable && licenseDuplicate && (
                    <div className='ms-buybox__license-duplicate'>
                        <RichTextComponent className='ms-buybox__license-duplicate-text' text={props.config.licenseDuplicate} />
                    </div>
                )}

                {/* @CUSTOM: Signed out addtocart replacement message */}
                {!unbuyable && !licenseDuplicate && !isSignedIn && (
                    <div className='ms-buybox__signed-out-message'>
                        <RichTextComponent className='ms-buybox__signedout-message-text' text={props.config.signedOutMessage} />
                    </div>
                )}

                {/* @CUSTOM: Moved elements below inside a container div */}
                {!unbuyable && !licenseDuplicate && isSignedIn && <div className='ms-buybox__action-group'>
                    {quantity && _renderQuantity(quantity)}
                    {inventoryLabel}
                    {addToCart && _renderAddToCart(addToCart)}
                    {findInStore && _renderFindInStore(findInStore)}
                    {addToOrderTemplate && _renderAddToOrderTemplate(addToOrderTemplate)}
                    {addToWishlist && _renderAddToWishlist(addToWishlist)}
                </div>}
                {/* @CUSTOM: Blacklisted shipping areas */}
                {props.config.blacklistedShippingAreasText && productAttributes && productAttributes[attrNames.blacklistedShippingAreas] && (
                    <div className='ms-buybox__blacklisted-areas'>
                        <RichTextComponent
                            className='ms-buybox__blacklisted-areas-text'
                            text={varReplace(props.config.blacklistedShippingAreasText as string, { areas: productAttributes[attrNames.blacklistedShippingAreas] as string })}
                        />
                    </div>
                )}
                {_renderSocialShare(props.slots && props.slots.socialShare)}
                {productComparisonButton}
                {shopSimilarLook && _renderShopSimilarItem(shopSimilarLook)}
                {shopSimilarDescription && _renderShopSimilarItem(shopSimilarDescription)}
                {/* @CUSTOM: Installment calculator */}
                {slots.installmentCal?.length && _renderSlotItems(slots.installmentCal)}
            </Node>
        </Module>
    );
};

const _renderAddToCart = (addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToCart;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

const _renderAddToOrderTemplate = (addToOrderTemplate: IBuyboxAddToOrderTemplateViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToOrderTemplate;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

const _renderAddToWishlist = (addToWishlist: IBuyboxAddToWishlistViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToWishlist;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

const _renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(_renderConfigureDropdown)}</Node>;
};

const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return <>{socialShare[0]}</>;
};

const _renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {select}
        </Node>
    );
};

const _renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    const { ContainerProps, storeSelector, heading, description, errors, button, modal, productPickupOptionList } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {productPickupOptionList}
            {description}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

// @CUSTOM: backorder customization
const _renderBackOrder = (productAvailableQuantity: IProductInventoryInformation[], text: RichText | undefined): JSX.Element | null => {

    const isAvail = productAvailableQuantity?.find(avail => {
        return avail.IsProductAvailable
    });

    if (isAvail) {
        return null;
    }

    return (
        <RichTextComponent className='ms-buybox__backorder-message' text={text} />
    );
};
//----------------------------------------------------------

const _renderQuantity = (quantity: IBuyboxProductQuantityViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input, errors } = quantity;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {input}
        </Node>
    );
};

const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

const _renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};

//----------------------------------------------------------
// @CUSTOM: Render slot items
//----------------------------------------------------------
const _renderSlotItems = (items: React.ReactNode[]): JSX.Element => {
    return (
        <>
            {items.map((slot: React.ReactNode, index: number) => (
                <React.Fragment key={index}>
                    {slot}
                </React.Fragment>
            ))}
        </>
    );
}
//----------------------------------------------------------

export default BuyboxView;
